import React, { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { IMsgItem, MsgDataProps } from ".";
import { useChatStore } from "@obsbot/remo-stores";
import { ICustomPayloadDescription } from "@obsbot/remo-services";
import { Avatar, List } from "antd";
import TencentCloudChat from "@tencentcloud/chat";
import { UserOutlined } from "@ant-design/icons";
import { formatShowTime } from "../../../utils/util";
import { MsgContent } from "./GroupMsg";
import { usePushHistory } from "@obsbot/remo-pages/src/utils/routes";
import { eventBus } from "@obsbot/remo-utils";
import { useParams } from "react-router-dom";
import { debounce } from "lodash-es";
import { useTranslate } from "@obsbot/remo-i18n";
import { keyTypeMap } from "../ChatPrivateTop";

export interface SearchMsgProps {
    searchData?: IMsgItem[] | null;
    isOpenMsg?: boolean;
    conversationID: string;
    activeTab?: string;
}

export const SearchMsg: React.FC<SearchMsgProps> = ({
    searchData,
    isOpenMsg,
    conversationID,
    activeTab,
}): ReactElement => {
    const { rtm } = useChatStore();
    const t = useTranslate();
    const params = useParams();
    const pushHistory = usePushHistory();
    const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const isLoadingRef = useRef(false);
    const isLoadingMore = useRef(false);
    const [msgData, setMsgData] = useState<MsgDataProps>({
        hasMore: false,
        chatData: [],
        nextReqMessageID: "",
    });

    const getMessageList = useCallback(
        async (curChatData: IMsgItem[], nextReqMessageID?: string): Promise<void> => {
            if (rtm && conversationID) {
                const res = await rtm.getMessageList(conversationID, nextReqMessageID);
                setMsgData({
                    hasMore: !res.isCompleted,
                    chatData: [...res.messageList, ...curChatData]
                        .filter(
                            message =>
                                message.payload?.description !==
                                    ICustomPayloadDescription.ChannelCommand && !message.isDeleted,
                        )
                        .sort((a, b) => b.time - a.time),
                    nextReqMessageID: res.nextReqMessageID,
                });
            }
        },
        [rtm, conversationID],
    );

    // 处理删除消息记录
    const clearMessageList = (eventData: { userID: string }): void => {
        if (eventData.userID === params.id) {
            getMessageList([]);
        }
    };
    useEffect(() => {
        eventBus.on("clear-history-msg", clearMessageList);
        return () => {
            eventBus.off("clear-history-msg", clearMessageList);
        };
    });
    // 处理接收到/发送消息
    useEffect(() => {
        const handleSendMsg = async (eventData: { userID: string }): Promise<void> => {
            const tempConversionID = "C2C" + eventData.userID;
            if (rtm && conversationID === tempConversionID) {
                getMessageList([]);
                rtm.setMessageRead(conversationID);
            }
        };
        eventBus.on("send-new-msg", handleSendMsg);
        return () => {
            eventBus.off("send-new-msg", handleSendMsg);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rtm, conversationID]);
    useEffect(() => {
        const handleReceivedMsg = (message: any): void => {
            if (message.conversationID === conversationID) {
                getMessageList([]);
                rtm?.setMessageRead(conversationID);
            }
        };
        rtm?.events.on("message-received", handleReceivedMsg);

        return () => {
            rtm?.events.off("message-received", handleReceivedMsg);
        };
    }, [rtm, conversationID]);

    useEffect(() => {
        getMessageList([], "");
    }, [isOpenMsg, getMessageList]);

    const shouldScrollToBottomRef = useRef(shouldScrollToBottom);
    useEffect(() => {
        shouldScrollToBottomRef.current = shouldScrollToBottom;
    }, [shouldScrollToBottom]);

    // 处理接收到消息后滚动到底部
    useEffect(() => {
        eventBus.on("message-received", debouncedScrollToBottom);
        return () => {
            eventBus.off("message-received", debouncedScrollToBottom);
        };
    }, []);

    const handleClickMsg = (info: IMsgItem): void => {
        const curChatData = { ...msgData, chatData: [...msgData.chatData].reverse() };
        eventBus.emit("scroll-to-msg", {
            msgID: info.ID,
            msgData: curChatData,
        });
    };

    // 防抖的滚动到底部函数
    const debouncedScrollToBottom = useCallback(
        debounce(() => {
            const container = containerRef.current;
            if (container && shouldScrollToBottomRef.current) {
                const attemptScroll = (retries = 3): void => {
                    if (retries <= 0) {
                        return;
                    }
                    const lastMessage = container.lastElementChild;
                    if (lastMessage) {
                        lastMessage.scrollIntoView({
                            behavior: "smooth",
                            block: "end",
                            inline: "nearest",
                        });
                    }
                    container.scrollTop = container.scrollHeight;
                    // 延迟一小段时间后再次检查是否真的在底部
                    setTimeout(() => {
                        const isAtBottom =
                            container.scrollHeight - container.scrollTop - container.clientHeight <
                            10; // 更小的容差
                        if (!isAtBottom && shouldScrollToBottomRef.current) {
                            // console.log(`Retry scrolling, attempt: ${retries}`);
                            attemptScroll(retries - 1);
                        }
                    }, 50); // 50ms 延迟
                };
                attemptScroll();
            }
        }, 300),
        [],
    );
    // 滚动事件处理函数
    const handleScroll = useCallback(
        debounce(async (): Promise<void> => {
            if (containerRef.current && !isLoadingRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
                // 判断是否滚动到底部，并检查是否还有更多数据且当前不在加载中
                const isAtBottom = scrollHeight - scrollTop - clientHeight < 10; // 10为容差
                if (isAtBottom && msgData.hasMore) {
                    isLoadingRef.current = true;
                    isLoadingMore.current = true;
                    try {
                        await getMessageList(msgData.chatData, msgData.nextReqMessageID);
                    } catch (error) {
                        console.error("加载更多消息失败:", error);
                    } finally {
                        isLoadingRef.current = false;
                    }
                }
            }
        }, 200),
        [msgData.hasMore, msgData.nextReqMessageID],
    );
    return (
        <div
            ref={containerRef}
            className="search-msg search-msg-list-container"
            onScroll={handleScroll}
            onWheel={() => setShouldScrollToBottom(false)}
        >
            <List
                dataSource={
                    searchData !== undefined && (searchData === null || searchData.length !== 0)
                        ? searchData ?? []
                        : msgData.chatData.filter(message =>
                              activeTab === "all"
                                  ? true
                                  : message.type === keyTypeMap[activeTab ?? ""],
                          )
                }
                locale={{
                    emptyText: t("chat.no-message"),
                }}
                renderItem={item =>
                    item.payload?.description === ICustomPayloadDescription.ChannelCommand ||
                    item.payload?.description === "" ? null : (
                        <List.Item
                            key={item.sequence}
                            className={`search-msg-item search-msg-item-type-${item.type}`}
                            onClick={handleClickMsg.bind(this, item)}
                        >
                            <List.Item.Meta
                                avatar={
                                    item.type !== TencentCloudChat.TYPES.MSG_GRP_SYS_NOTICE &&
                                    item.type !== TencentCloudChat.TYPES.MSG_GRP_TIP ? (
                                        <Avatar
                                            size={42}
                                            src={
                                                item.avatar || (
                                                    <UserOutlined style={{ fontSize: 24 }} />
                                                )
                                            }
                                        />
                                    ) : null
                                }
                                description={
                                    <MsgContent
                                        basicInfo={{
                                            from: item.from,
                                            fromNick: item.nick,
                                            to: item.to,
                                            flow: item.flow,
                                        }}
                                        isSearch={true}
                                        msg={item}
                                        msgData={msgData}
                                        pushHistory={pushHistory}
                                        setMsgData={setMsgData}
                                    />
                                }
                                title={
                                    <div className="search-msg-item-title">
                                        <div className="name">{item.nick}</div>
                                        <div className="time">{formatShowTime(item.time)}</div>
                                    </div>
                                }
                            />
                        </List.Item>
                    )
                }
            ></List>
        </div>
    );
};
